import {
  alpha,
  Box,
  Divider,
  hexToRgb,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import StarRating from '../StarRating/StarRating';
import { OpenInNew } from '@mui/icons-material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import NumberUtils from '../../shared/NumberUtils';

interface Props {
  companyName: string;
  companyLogo: string;
  score: number;
  reviewCount: number;
  companyWebsiteUrl: string;
}

const TrustpageHeader = ({
  companyName,
  companyLogo,
  score,
  reviewCount,
  companyWebsiteUrl,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.up('md'));

  const shortUrl = companyWebsiteUrl.slice(0, 30);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        minHeight: 128,
        p: theme.spacing(2),
        backgroundColor: '#FFFFFF',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          minWidth: 'auto',
          maxWidth: '1366px',
          flex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {companyLogo && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(hexToRgb(theme.palette.primary.main), 0.2),
                minHeight: isSmallScreen ? 128 : 60,
                maxHeight: isSmallScreen ? 128 : 60,
                minWidth: isSmallScreen ? 128 : 60,
                borderRadius: theme.spacing(2),
                p: theme.spacing(2),
              }}
            >
              <img
                src={companyLogo}
                style={{
                  maxHeight: isSmallScreen ? 64 : 32,
                  maxWidth: isSmallScreen ? 200 : 100,
                }}
                alt={`${companyName} ${t('logo_label')}`}
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              p: theme.spacing(2),
            }}
          >
            <Typography variant="h6">{companyName}</Typography>
            <Box sx={{ display: 'flex', mt: theme.spacing(1), alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {t('reputation_score_label')}
                </Typography>
                <StarRating value={score} />
                <Typography
                  variant="caption"
                  sx={{ alignSelf: 'center' }}
                >
                  {`${NumberUtils.formatScore(score)} ${t('of')} 5 ${t('stars')}`}
                </Typography>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: theme.spacing(2) }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {t('nav_reviews')}
                </Typography>
                <Typography variant="body2">{reviewCount}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: '#f1f1f1',
            borderRadius: theme.spacing(2),
            px: theme.spacing(2),
            py: theme.spacing(1),
            display: isSmallScreen ? 'block' : 'none',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {t('website')}
          </Typography>
          <Link
            href={companyWebsiteUrl}
            variant="body1"
            underline="none"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {shortUrl}
            <OpenInNew
              fontSize="small"
              sx={{ ml: theme.spacing(1) }}
            />
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default TrustpageHeader;
